import { useState, useEffect } from "react";
import {
  featureToggleManager,
  FeatureToggleKey,
  FEATURE_TOGGLES,
} from "src/lib/featureToggles";

/**
 * Hook to get the state of a specific feature toggle
 * Uses event-driven updates instead of polling for better performance
 * Can accept either a string key or the feature toggle object directly
 */
export function useFeatureToggle(featureKey: FeatureToggleKey): boolean {
  const [isEnabled, setIsEnabled] = useState(() =>
    featureToggleManager.isFeatureEnabled(featureKey),
  );

  useEffect(() => {
    // Update initial state
    setIsEnabled(featureToggleManager.isFeatureEnabled(featureKey));

    // Subscribe to real-time updates (replaces polling)
    const unsubscribe = featureToggleManager.addListener(() => {
      setIsEnabled(featureToggleManager.isFeatureEnabled(featureKey));
    });

    return unsubscribe;
  }, [featureKey]);

  return isEnabled;
}

/**
 * Simple helper to check if a feature is enabled
 * For use inside components with hooks - reactive and updates UI
 * Can accept either string key or feature toggle object directly
 */
export function useIsOn(
  feature:
    | FeatureToggleKey
    | (typeof FEATURE_TOGGLES)[keyof typeof FEATURE_TOGGLES],
): boolean {
  // If it's a feature toggle object, extract the key
  const featureKey =
    typeof feature === "string"
      ? feature
      : (Object.entries(FEATURE_TOGGLES).find(
          ([_, obj]) => obj === feature,
        )?.[0] as FeatureToggleKey);

  return useFeatureToggle(featureKey);
}

/**
 * Hook for accessing all feature toggle states
 * Useful for debug panels or bulk feature checking
 */
export function useAllFeatureToggles(): Record<string, boolean> {
  const [allStates, setAllStates] = useState(() =>
    featureToggleManager.getAllFeatureStates(),
  );

  useEffect(() => {
    // Update initial state
    setAllStates(featureToggleManager.getAllFeatureStates());

    // Subscribe to real-time updates
    const unsubscribe = featureToggleManager.addListener(() => {
      setAllStates(featureToggleManager.getAllFeatureStates());
    });

    return unsubscribe;
  }, []);

  return allStates;
}

/**
 * Hook for feature toggle manager status
 * Useful for loading states
 */
export function useFeatureToggleManager() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Check if already initialized using proper getter
    setIsInitialized(featureToggleManager.initialized);

    // Listen for initialization updates
    const unsubscribe = featureToggleManager.addListener(() => {
      setIsInitialized(featureToggleManager.initialized);
    });

    return unsubscribe;
  }, []);

  return {
    isInitialized,
    manager: featureToggleManager,
  };
}
